import { api } from '../api.js';
import { h, money, moneyCents, shortDate, openSheet } from '../ui.js';

// Recurring: the subscriptions you track, then the charges that recur on their own
// (found automatically: a loan, insurance, copays). Each shows what it costs a month,
// when it should come next, and flags a new charge, a price change or one that stopped.

interface Item {
  description: string; subscription: boolean; cadence: 'monthly' | 'yearly' | 'irregular';
  amount: number; monthly: number; last_date: string; last_amount: number; next_date: string | null;
  price_change: { from: number; to: number } | null; status: 'active' | 'new' | 'stopped';
  account: string; count: number;
}
interface Data {
  items: Item[];
  totals: { subscriptions_monthly: number; subscriptions_count: number; recurring_monthly: number };
}

export function recurringScreen(): HTMLElement {
  const root = h('div', 'screen');
  const top = h('div', 'topbar');
  const body = h('div');
  body.appendChild(h('div', 'loading', 'Loading…'));
  top.appendChild(h('div', 'title', 'Recurring'));
  root.append(top, body);

  async function load(): Promise<void> {
    try {
      render(await api.get<Data>('/recurring'));
    } catch (err) {
      if (err instanceof Error && err.message === 'Unauthorized') return;
      body.innerHTML = '';
      body.appendChild(h('div', 'error', err instanceof Error ? err.message : 'Could not load'));
    }
  }

  function render(d: Data): void {
    body.innerHTML = '';
    const pair = h('div', 'pair');
    pair.style.marginTop = '20px';
    const subs = h('div');
    subs.append(h('div', 'label', 'Subscriptions a month'), h('div', 'money', money(d.totals.subscriptions_monthly)));
    const year = h('div');
    year.append(h('div', 'label', 'A year'), h('div', 'money', money(d.totals.subscriptions_monthly * 12)));
    pair.append(subs, year);
    body.appendChild(pair);

    section('Subscriptions', d.items.filter(i => i.subscription && i.status !== 'stopped'));
    section('Other recurring', d.items.filter(i => !i.subscription && i.status !== 'stopped'),
      `${money(d.totals.recurring_monthly)} a month, found automatically. Tap one to track it as a subscription.`);
    section('Stopped', d.items.filter(i => i.status === 'stopped'), 'No charge when the next one was due.');
  }

  function section(title: string, items: Item[], note?: string): void {
    if (items.length === 0) return;
    body.appendChild(h('div', 'section-title', title));
    if (note) body.appendChild(h('div', 'row-sub', note));
    for (const i of items) body.appendChild(itemRow(i));
  }

  function itemRow(i: Item): HTMLElement {
    // After a price change the current price is the last charge, not the usual one.
    const price = i.price_change ? i.last_amount : i.amount;
    const row = h('button', 'row');
    row.style.width = '100%';
    row.style.textAlign = 'left';
    const main = h('div', 'row-main');
    const name = h('div', '', i.description);
    if (i.status === 'new') name.appendChild(h('span', 'tag', 'new'));
    if (i.price_change) name.appendChild(h('span', 'tag', i.price_change.to > i.price_change.from ? 'price up' : 'price down'));
    main.appendChild(name);
    const bits = [
      i.cadence === 'yearly' ? `${moneyCents(price)} a year` : i.cadence === 'monthly' ? `${moneyCents(price)} monthly` : `about ${moneyCents(price)}, irregular`,
      i.price_change ? `was ${moneyCents(i.price_change.from)}` : null,
      i.next_date ? `next ~${shortDate(i.next_date)}` : `last ${shortDate(i.last_date)}`,
      i.account.split(' ')[0],
    ].filter(Boolean);
    main.appendChild(h('div', 'row-sub', bits.join(' · ')));
    const right = h('div', 'acct-right');
    right.append(h('div', 'money', money(i.monthly)), h('div', 'acct-chg muted', 'a month'));
    row.append(main, right);
    row.addEventListener('click', () => toggleSheet(i));
    return row;
  }

  function toggleSheet(i: Item): void {
    openSheet((sheet, close) => {
      sheet.append(h('h3', '', i.description),
        h('div', 'sub', `${i.count} charges · last ${moneyCents(i.last_amount)} on ${shortDate(i.last_date)} ${i.last_date.slice(0, 4)}`));
      const btn = h('button', i.subscription ? 'btn' : 'btn primary', i.subscription ? 'Not a subscription' : 'Track as a subscription');
      btn.style.width = '100%';
      btn.style.marginTop = '16px';
      btn.addEventListener('click', async () => {
        btn.disabled = true;
        await api.post('/subscriptions', { description: i.description, value: !i.subscription });
        close();
        void load();
      });
      sheet.appendChild(btn);
    });
  }

  void load();
  return root;
}
